import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'src/utils/Hooks';
import { BeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { getAtlasSalesV2Treatment, getTrafficV2Treatment, getAtlasConversionV2Treatment } from 'src/utils/app';
import { ATLAS_TABS, ATLAS_SUBTABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

/*
  Redirect rules
  - scorecard <-> scorecardV2: controlled by getAtlasSalesV2Treatment
  - sales <-> salesV2: controlled by getAtlasSalesV2Treatment
  - prices <-> pricesV2: controlled by getAtlasSalesV2Treatment
  - content <-> contentV2: controlled by getAtlasSalesV2Treatment
  - rating <-> ratingV2: controlled by getAtlasSalesV2Treatment
  - traffic <-> trafficV2: controlled by getTrafficV2Treatment
  - conversion <-> conversionV2: currently gated by Sales V2 in routes; follow getAtlasSalesV2Treatment
*/

type Treatment = 'off' | 'on' | 'both';

type TabPair = {
  v1: string;
  v2: string;
  getTreatment: () => Treatment;
  defaultSubtab?: string;
  // Optional mapping for subtab fallbacks when v2 has subtabs not present in v1
  v2OnlySubtabsFallback?: { [subtab: string]: string };
};

const TAB_PAIRS: TabPair[] = [
  {
    v1: ATLAS_TABS.SUMMARY,
    v2: ATLAS_TABS.SUMMARY_V2,
    getTreatment: getAtlasSalesV2Treatment,
    defaultSubtab: ATLAS_SUBTABS.KEY_METRICS
  },
  { v1: ATLAS_TABS.SALES, v2: ATLAS_TABS.SALES_V2, getTreatment: getAtlasSalesV2Treatment },
  { v1: ATLAS_TABS.CONVERSION, v2: ATLAS_TABS.CONVERSION_V2, getTreatment: getAtlasSalesV2Treatment },
  { v1: ATLAS_TABS.PRICES, v2: ATLAS_TABS.PRICES_V2, getTreatment: getAtlasSalesV2Treatment },
  { v1: ATLAS_TABS.CONTENT, v2: ATLAS_TABS.CONTENT_V2, getTreatment: getAtlasSalesV2Treatment },
  { v1: ATLAS_TABS.RATING, v2: ATLAS_TABS.RATING_V2, getTreatment: getAtlasSalesV2Treatment },
  {
    v1: ATLAS_TABS.TRAFFIC,
    v2: ATLAS_TABS.TRAFFIC_V2,
    getTreatment: getTrafficV2Treatment,
    defaultSubtab: ATLAS_SUBTABS.TOTAL_CLICKS,
    v2OnlySubtabsFallback: { [ATLAS_SUBTABS.OTHER]: ATLAS_SUBTABS.TOTAL_CLICKS }
  }
];

const getPairForTab = (tab: string): TabPair | undefined => TAB_PAIRS.find((p) => p.v1 === tab || p.v2 === tab);

export const useAtlasV2TabTreatmentRedirect = () => {
  const history = useHistory();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentTab = searchParams.get('tab') || '';
  const currentSubtab = searchParams.get('subtab') || '';
  const entityType = useMainEntityType();

  useEffect(() => {
    // Skip redirection if entityType is searchtermlist
    if (entityType === 'searchtermlist') return;

    const pair = getPairForTab(currentTab);
    if (!pair) return;

    const treatment = pair.getTreatment();

    // Determine desired tab based on treatment
    let targetTab = currentTab;
    if (treatment === 'on' && currentTab === pair.v1) {
      targetTab = pair.v2;
    } else if (treatment === 'off' && currentTab === pair.v2) {
      targetTab = pair.v1;
    } else {
      // 'both' or already aligned with treatment → do nothing
      return;
    }

    // Compute target subtab
    let targetSubtab = currentSubtab || pair.defaultSubtab || '';

    // If redirecting from v2 to v1, and subtab exists only in v2, apply fallback
    if (currentTab === pair.v2 && targetTab === pair.v1 && pair.v2OnlySubtabsFallback) {
      if (pair.v2OnlySubtabsFallback[currentSubtab]) {
        targetSubtab = pair.v2OnlySubtabsFallback[currentSubtab];
      }
    }

    // If no subtab in URL and we have a default for the target, use it - traffic other
    if (!targetSubtab && pair.defaultSubtab) {
      targetSubtab = pair.defaultSubtab;
    }

    setIsRedirecting(true);
    const beaconNavigator = new BeaconNavigator(new URLSearchParams(location.search), history);
    const update: Record<string, string> = { tab: targetTab };
    if (targetSubtab) update.subtab = targetSubtab;
    beaconNavigator.updateSearchParams(update).go();
  }, [currentTab, currentSubtab, entityType, history, location.search]);

  return { isRedirecting };
};
